/**
 * The technology tree.
 *
 * The screen answers four questions, in the order a player asks them: what am I
 * learning and when will it land, what could I learn next, what is further off and how
 * do I get there, and what am I paying for it. The last is why the science slider lives
 * here as well as on the top bar — the choice of rate only means something next to what
 * it buys.
 *
 * One click does the right thing whatever is clicked. A technology that is ready is
 * researched now; one further off becomes a GOAL, and the way there is researched step
 * by step without being asked again (see src/core/research). With eighty technologies,
 * choosing each by hand would be the chore that makes a long game tiresome.
 */

import type { Engine } from '../core/engine.js';
import { budget } from '../core/cities/economy.js';
import { canResearch, currentEra, knows, pathTo, techCost, turnsToDiscover, unlocksOf } from '../core/research/index.js';
import { NONE } from '../core/state/types.js';
import { ERROR_TEXT } from '../core/commands/index.js';
import { layoutTechTree, type TechLayout } from './tech-layout.js';

export interface TechScreen {
  open(focus?: number): void;
  close(): void;
  readonly isOpen: boolean;
  refresh(): void;
}

export interface TechScreenOptions {
  getEngine: () => Engine;
  /** Something changed that the rest of the interface should reflect. */
  onChanged: (message: string, kind?: 'ok' | 'err' | 'note') => void;
}

/** Card geometry, in CSS pixels. */
const CARD_W = 168;
const CARD_H = 60;
const COL_W = 212;
const ROW_H = 72;
const PAD_X = 24;
const PAD_TOP = 40;

export function createTechScreen(options: TechScreenOptions): TechScreen {
  const root = document.createElement('div');
  root.className = 'tech-screen';
  root.hidden = true;
  root.innerHTML = `
    <div class="tech-panel">
      <header>
        <h2>Technology</h2>
        <div class="tech-status" id="ts-status"></div>
        <div class="tech-rate">
          <span>Science</span>
          <button id="ts-down" title="Less science, more gold">−</button>
          <strong id="ts-rate"></strong>
          <button id="ts-up" title="More science, less gold">+</button>
          <span class="tech-flow" id="ts-flow"></span>
        </div>
        <button id="ts-close" title="Close (Esc)">Close</button>
      </header>
      <div class="tech-scroll" id="ts-scroll"><div class="tech-canvas" id="ts-canvas"></div></div>
      <footer class="tech-detail" id="ts-detail"></footer>
    </div>
  `;
  document.body.append(root);

  const $ = <T extends HTMLElement>(id: string): T => root.querySelector(`#${id}`) as T;
  let layout: TechLayout | null = null;
  let hovered = NONE;

  const x = (col: number): number => PAD_X + col * COL_W;
  const y = (row: number): number => PAD_TOP + row * ROW_H;

  function describe(engine: Engine, id: number): string {
    const { content } = engine;
    const tech = content.technologies.get(id);
    const civ = engine.state.civs[engine.activeCiv];
    const era = content.eras.get(tech.eraIndex).name;
    const needs = tech.prerequisites.map((p) => content.technologies.get(p).name);
    const leads = content.technologies.all.filter((t) => t.prerequisites.includes(id)).map((t) => t.name);
    const unlocks = unlocksOf(content, id);
    const kinds = { unit: 'unit', building: 'building', improvement: 'improvement', government: 'government', resource: 'revealed' } as const;
    let state = '';
    if (civ && knows(civ, id)) state = 'Known.';
    else if (civ && canResearch(content, civ, id)) state = 'Ready to research — click to begin.';
    else if (civ) {
      const steps = pathTo(content, civ, id).length;
      state =
        civ.researchGoal === id
          ? `Your goal — ${steps} discoveries away, researched in turn without being asked again.`
          : `${steps} discoveries away — click to make it your goal.`;
    }
    return (
      `<strong>${escapeHtml(tech.name)}</strong> <span class="dim">${escapeHtml(era)} · ${techCost(content, id)} science</span>` +
      `<div>${escapeHtml(tech.description)}</div>` +
      (unlocks.length
        ? `<div class="unlocks">Unlocks ${unlocks.map((u) => `<b>${escapeHtml(u.name)}</b> <span class="dim">${kinds[u.kind]}</span>`).join(', ')}</div>`
        : '') +
      `<div class="dim">${needs.length ? `Requires ${needs.map(escapeHtml).join(', ')}.` : 'A first step — requires nothing.'} ` +
      `${leads.length ? `Leads to ${leads.map(escapeHtml).join(', ')}.` : ''} ${state}</div>`
    );
  }

  function render(): void {
    const engine = options.getEngine();
    const { state, content } = engine;
    const civ = state.civs[engine.activeCiv];
    if (!civ) return;
    layout ??= layoutTechTree(content);

    const accounts = budget(state, content, engine.activeCiv);
    const goalPath = new Set(civ.researchGoal === NONE ? [] : pathTo(content, civ, civ.researchGoal));

    // ── header ──
    const researching = civ.researching === NONE ? null : content.technologies.get(civ.researching);
    const turns = turnsToDiscover(content, civ, accounts.science);
    let status: string;
    if (researching) {
      const cost = techCost(content, researching.id);
      status =
        `Researching <strong>${escapeHtml(researching.name)}</strong> · ${Math.min(civ.researchStore, cost)}/${cost}` +
        (turns === null ? ' · no science coming in' : ` · ${turns} ${turns === 1 ? 'turn' : 'turns'}`);
    } else {
      status = `<span class="warn">Nothing being researched — choose a technology below.</span>`;
      if (civ.researchStore > 0) status += ` <span class="dim">${civ.researchStore} science waiting</span>`;
    }
    if (civ.researchGoal !== NONE) {
      status += ` <span class="dim">· goal ${escapeHtml(content.technologies.get(civ.researchGoal).name)} (${goalPath.size} to go)</span>`;
    }
    status += ` <span class="dim">· ${escapeHtml(content.eras.get(currentEra(content, civ)).name)} era · ${civ.techs.length} of ${content.technologies.all.length} known</span>`;
    $('ts-status').innerHTML = status;
    $('ts-rate').textContent = `${civ.researchRate}%`;
    $('ts-flow').textContent = `+${accounts.science} science · ${accounts.net >= 0 ? '+' : ''}${accounts.net} gold`;
    $('ts-flow').className = `tech-flow ${accounts.net < 0 ? 'warn' : ''}`;
    ($('ts-down') as HTMLButtonElement).disabled = civ.researchRate <= 0;
    ($('ts-up') as HTMLButtonElement).disabled = civ.researchRate >= content.rules.research.maxRate;

    // ── the tree ──
    const width = PAD_X * 2 + (layout.columns - 1) * COL_W + CARD_W;
    const height = PAD_TOP + layout.rows * ROW_H + 12;
    const canvas = $('ts-canvas');
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const bands = layout.bands
      .map((band, i) => {
        const left = x(band.firstCol) - (COL_W - CARD_W) / 2;
        const right = x(band.lastCol) + CARD_W + (COL_W - CARD_W) / 2;
        const era = content.eras.get(band.era);
        return `<div class="tech-band ${i % 2 ? 'odd' : ''}" style="left:${left}px;width:${right - left}px;height:${height}px">
          <span>${escapeHtml(era.name)}</span></div>`;
      })
      .join('');

    const edges: string[] = [];
    for (const tech of content.technologies.all) {
      const to = layout.place[tech.id]!;
      for (const p of tech.prerequisites) {
        const from = layout.place[p]!;
        const x1 = x(from.col) + CARD_W;
        const y1 = y(from.row) + CARD_H / 2;
        const x2 = x(to.col);
        const y2 = y(to.row) + CARD_H / 2;
        const bend = Math.max(24, (x2 - x1) * 0.5);
        const cls = knows(civ, tech.id) ? 'known' : goalPath.has(tech.id) && (knows(civ, p) || goalPath.has(p)) ? 'path' : knows(civ, p) ? 'open' : '';
        edges.push(`<path class="${cls}" d="M${x1},${y1} C${x1 + bend},${y1} ${x2 - bend},${y2} ${x2},${y2}" />`);
      }
    }

    const cards = content.technologies.all
      .map((tech) => {
        const at = layout!.place[tech.id]!;
        const cost = techCost(content, tech.id);
        let cls: string;
        let line: string;
        let progress = 0;
        if (knows(civ, tech.id)) {
          cls = 'known';
          line = 'known';
        } else if (civ.researching === tech.id) {
          cls = 'researching';
          progress = Math.min(1, civ.researchStore / cost);
          line = turns === null ? `${cost} · stalled` : `researching · ${turns}t`;
        } else if (canResearch(content, civ, tech.id)) {
          cls = 'ready';
          const est = accounts.science > 0 ? Math.ceil(cost / accounts.science) : null;
          line = est === null ? `${cost}` : `${cost} · ~${est}t`;
        } else {
          cls = 'locked';
          line = `${cost}`;
        }
        if (goalPath.has(tech.id)) cls += ' on-path';
        if (civ.researchGoal === tech.id) cls += ' goal';
        return `<button class="tech-card ${cls}" data-tech="${tech.id}" style="left:${x(at.col)}px;top:${y(at.row)}px;width:${CARD_W}px;height:${CARD_H}px">
          <span class="tn">${escapeHtml(tech.name)}</span>
          <span class="tl">${civ.researchGoal === tech.id ? 'goal · ' : ''}${line}</span>
          <span class="tu">${escapeHtml(unlocksOf(content, tech.id).map((u) => u.name).join(' · '))}</span>
          ${progress > 0 ? `<i style="width:${Math.round(progress * 100)}%"></i>` : ''}
        </button>`;
      })
      .join('');

    canvas.innerHTML =
      bands + `<svg class="tech-edges" width="${width}" height="${height}">${edges.join('')}</svg>` + cards;

    for (const card of canvas.querySelectorAll<HTMLButtonElement>('.tech-card')) {
      const id = Number(card.dataset['tech']);
      card.addEventListener('mouseenter', () => {
        hovered = id;
        $('ts-detail').innerHTML = describe(engine, id);
      });
      card.addEventListener('click', () => choose(id));
    }

    const detailFor = hovered !== NONE ? hovered : civ.researching !== NONE ? civ.researching : NONE;
    $('ts-detail').innerHTML =
      detailFor === NONE ? '<span class="dim">Hover over a technology to read about it. Click one to research it, or to make it your goal.</span>' : describe(engine, detailFor);
  }

  function choose(id: number): void {
    const engine = options.getEngine();
    const civ = engine.state.civs[engine.activeCiv];
    if (!civ || knows(civ, id)) return;
    const result = engine.apply({ t: 'SetResearchGoal', tech: id });
    if (!result.ok) {
      options.onChanged(ERROR_TEXT[result.error], 'err');
      return;
    }
    const name = engine.content.technologies.get(id).name;
    options.onChanged(civ.researchGoal === id ? `Working towards ${name}.` : `Researching ${name}.`, 'ok');
    render();
  }

  function stepRate(direction: 1 | -1): void {
    const engine = options.getEngine();
    const civ = engine.state.civs[engine.activeCiv];
    if (!civ) return;
    const rate = civ.researchRate + direction * engine.content.rules.research.rateStep;
    const result = engine.apply({ t: 'SetScienceRate', rate });
    if (!result.ok) return;
    options.onChanged('', 'note');
    render();
  }

  $('ts-close').addEventListener('click', () => close());
  $('ts-down').addEventListener('click', () => stepRate(-1));
  $('ts-up').addEventListener('click', () => stepRate(1));
  root.addEventListener('click', (event) => {
    if (event.target === root) close();
  });

  let isOpen = false;

  function open(focus?: number): void {
    isOpen = true;
    root.hidden = false;
    hovered = NONE;
    render();

    // Put the frontier in view: the current research, or else the given technology, or
    // else the cheapest thing ready — never the left edge of an eighty-card tree.
    const engine = options.getEngine();
    const civ = engine.state.civs[engine.activeCiv];
    const target = focus ?? (civ && civ.researching !== NONE ? civ.researching : null);
    const scroll = $('ts-scroll');
    let col = 0;
    let row = 0;
    if (target !== null && target !== undefined && layout) {
      col = layout.place[target]!.col;
      row = layout.place[target]!.row;
    } else if (civ && layout) {
      const ready = engine.content.technologies.all.filter((t) => canResearch(engine.content, civ, t.id));
      if (ready.length) {
        col = Math.min(...ready.map((t) => layout!.place[t.id]!.col));
        row = layout.place[ready[0]!.id]!.row;
      }
    }
    scroll.scrollLeft = Math.max(0, x(col) - scroll.clientWidth * 0.3);
    scroll.scrollTop = Math.max(0, y(row) - scroll.clientHeight * 0.4);
  }

  function close(): void {
    isOpen = false;
    root.hidden = true;
  }

  return {
    open,
    close,
    get isOpen() {
      return isOpen;
    },
    refresh: () => {
      if (isOpen) render();
    },
  };
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
